import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm" }
const _hoisted_2 = { class: "mb-4 flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "text-sm font-semibold text-gray-800" }
const _hoisted_4 = { class: "text-gray-400" }
const _hoisted_5 = {
  key: 0,
  class: "flex min-h-[320px] flex-1 items-center justify-center"
}
const _hoisted_6 = {
  key: 1,
  class: "flex min-h-[320px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-red-200 bg-red-50/40 px-4 text-center"
}
const _hoisted_7 = { class: "text-sm text-red-700" }
const _hoisted_8 = { class: "flex flex-wrap items-center justify-center gap-2" }
const _hoisted_9 = {
  key: 2,
  class: "flex min-h-[320px] flex-1 items-center justify-center rounded-xl border border-dashed border-gray-300 px-4 text-center"
}
const _hoisted_10 = { class: "text-sm italic text-gray-400" }
const _hoisted_11 = {
  key: 3,
  class: "flex min-h-0 flex-1 flex-col"
}
const _hoisted_12 = { class: "mb-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-gray-100 pb-3" }
const _hoisted_13 = { class: "flex flex-wrap items-center gap-1.5" }
const _hoisted_14 = {
  key: 0,
  class: "mx-1 h-4 w-px shrink-0 bg-gray-200",
  "aria-hidden": "true"
}
const _hoisted_15 = ["disabled", "aria-pressed", "onClick"]
const _hoisted_16 = ["aria-label", "onClick"]
const _hoisted_17 = { class: "flex flex-wrap items-center gap-4" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = {
  key: 1,
  class: "flex min-h-[320px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 px-4 text-center"
}
const _hoisted_20 = { class: "text-sm text-gray-500" }
const _hoisted_21 = {
  key: 3,
  class: "mt-1.5 text-xs leading-[18px]"
}

import { computed, ref, watch, type WritableComputedRef } from 'vue';
import { useLocalStorage, useWindowSize } from '@vueuse/core';
import { ChevronDown, Info } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Spinner from '@/components/ui/Spinner.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { i18n } from '@/plugins/internationalization/i18n';
import { useProductivity, useProductivityComparison, type ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import { ChartType } from '@/types';
import type { ProductionGroup } from '@/types/ProductionGroup';
import { cameraReadoutAt } from './cameraReadout';
import { drawsCameraSeries, resolveHiddenCameras, toggleHiddenCamera } from './cameraSeries';
import { countImplausible } from './plausibleRange';
import { periodPositionLabel, type ComparisonProjection } from './productivityAxis';
import { computeBandLayout } from './productivityBands';
import { buildProductivityOptions, productivityChartHeight, productivityPlotHeight } from './productivityChartOptions';
import { buildComparisonHaloSeries, comparisonField, projectedComparisonEndMs, resolveComparisonCandidates, resolveSelectedComparison } from './productivityComparison';
import ProductivityComparisonPicker from './ProductivityComparisonPicker.vue';
import { createProductivityEventTooltip } from './productivityEventTooltip';
import { bucketPeriodMs, bucketTimestamp, productivityInstant } from './productivityFormatting';
import {
    bandEmptinessFields,
    METRIC_KEYS,
    metricField,
    metricKeysInBand,
    partialPeriodBlankedFields,
    PRODUCTIVITY_BAND_IDS,
    PRODUCTIVITY_METRICS,
    type MetricBandId,
    type MetricFlags,
    type MetricKey,
    type MetricSensor,
    type ProductivityBucketField,
} from './productivityMetrics';
import { productivityOverlay } from './productivityOverlay';
import { RANGE_OPTIONS, type ProductivityRangeOption } from './productivityRanges';
import ProductivityReadoutRow from './ProductivityReadoutRow.vue';
import { productivityPlottableValue } from './productivitySeries';
import { useProductivityCursor } from './useProductivityCursor';
import { useProductivityViewState } from './useProductivityViewState';

// The two reference-coverage lines' key prefix, which is all they share.
const REFERENCE_T = 'views.monitoring.activeGroups.modals.productivity.compareReference';

const METRIC_CHIP_CLASS = 'inline-flex items-center rounded-full border text-xs font-medium transition-colors';
// On is filled in the solution's own colour, the same fill the granularity pill beside it already
// uses for its selected option, so "this is on" reads the same way in both controls. Off is the
// plain white outline: an empty box, which is what an unticked control looks like everywhere.
const METRIC_CHIP_ON_CLASS = 'border-primary-700 bg-primary-700 text-white shadow-sm hover:bg-primary-800';
const METRIC_CHIP_OFF_CLASS = 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50';

const TOGGLE_GROUP_CLASS = 'inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5';
const TOGGLE_BUTTON_CLASS = 'rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40';
const TOGGLE_BUTTON_ACTIVE_CLASS = 'bg-primary-700 text-white shadow-sm';
const TOGGLE_BUTTON_IDLE_CLASS = 'text-gray-600 hover:bg-white hover:text-gray-900';

export default /*@__PURE__*/_defineComponent({
  __name: 'ProductivityChart',
  props: {
    sectionId: {},
    productionGroupStart: {},
    productionGroupHistory: {},
    productionGroupHistoryUnavailable: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const sectionId = computed(() => props.sectionId);

const { rangeOption, range, granularity, granularityOptions, selectedComparisonId, hiddenCameraSensorIds } = useProductivityViewState({
    sectionId,
    productionGroupStart: computed(() => props.productionGroupStart),
});

const RANGE_OPTION_I18N_KEYS: Record<ProductivityRangeOption, string> = {
    productionGroup: 'views.monitoring.activeGroups.modals.productivity.rangeProductionGroup',
    '3m': 'views.monitoring.activeGroups.modals.productivity.range3Months',
    '6m': 'views.monitoring.activeGroups.modals.productivity.range6Months',
    '9m': 'views.monitoring.activeGroups.modals.productivity.range9Months',
    '12m': 'views.monitoring.activeGroups.modals.productivity.range12Months',
};

const { data, isLoading, isError, refetch } = useProductivity(sectionId, range, granularity);

// The axis's "now". Every instant on this chart is a Directory-local wall-clock reading stamped as
// UTC, and this is the one the server states for the same clock - so the axis edge, the open Study
// Period's band, the "Dag N" chip and the reference's past-now shading all measure from the farm's
// own present rather than from the reader's. Falls back to this machine's clock only before the
// first response, when nothing is drawn against it yet.
const nowMs = computed(() => (data.value ? productivityInstant(data.value.directory_local_now) : Date.now()));
const productionGroups = computed(() => data.value?.production_groups ?? []);

// The buckets as the panel reads them, which is the response's minus the figures a period still
// running cannot state yet. The rule and the reasoning are the registry's
// (`meaningfulForPartialPeriod`); this is the one place it is applied, deliberately upstream of
// everything rather than in the series builder, so the curve, the readout row, the band-emptiness
// test and the resting reading cannot disagree about whether today has a water figure. Blanked in
// the series alone, it leaves the cursor quoting the very number the chart declined to draw.
//
// Only ever the last bucket, and only when the farm's own present still falls inside its period -
// `nowMs` is the Directory's clock as the server states it, not the reader's, so a Section being
// looked at from another time zone blanks the same bucket its own barn would.
const buckets = computed(() => {
    const raw = data.value?.buckets ?? [];
    const blanked = partialPeriodBlankedFields(granularity.value);
    if (raw.length === 0 || blanked.length === 0) return raw;

    const last = raw[raw.length - 1];
    const start = bucketTimestamp(last);
    if (nowMs.value < start || nowMs.value >= start + bucketPeriodMs(granularity.value)) return raw;

    return [...raw.slice(0, -1), { ...last, ...Object.fromEntries(blanked.map((field) => [field, null])) }];
});
const feedMixes = computed(() => data.value?.feed_mixes ?? []);
const notes = computed(() => data.value?.notes ?? []);
const alarms = computed(() => data.value?.alarms ?? []);

// Non-physical derived points (plausibleRange.ts) are blanked before plotting, so the axis is
// fitted to the readings that mean something rather than to a restock artefact. Counted here so the
// chart can say how many it withheld, per metric - a caveat that names neither the metric nor the
// count is not much better than the silent gap it explains.
const withheldPointCaveats = computed(() => {
    const withheld = METRIC_KEYS.flatMap((key) => {
        const metric = PRODUCTIVITY_METRICS[key];
        if (metric.plausibleRange === null) return [];
        const field = metricField(key, granularity.value);
        return [
            {
                label: i18n.global.t(metric.labelKey) as string,
                count: countImplausible(buckets.value, (bucket) => bucket[field], metric.plausibleRange),
            },
        ];
    });

    return withheld
        .filter((metric) => metric.count > 0)
        .map(
            (metric) =>
                i18n.global.t('views.monitoring.activeGroups.texts.metricCaveat', {
                    metric: metric.label,
                    caveat: i18n.global.tc('views.monitoring.activeGroups.texts.chartImplausibleWarning', metric.count),
                }) as string
        );
});

// Where a loaded reference has no figure for a metric until some way into its own run - a weight
// Sensor fitted mid-batch, say - or has none at all. The halo then simply begins partway across the
// chart, or is missing while its toggle is on, and both read as something about the chart rather than
// about the batch: "the reference started here" and "this metric is switched off" are the wrong
// readings, and neither is recoverable from the picture. The readout row states the same absence as a
// dash under the cursor; this says it without having to be hunted for.
const referenceCoverageCaveats = computed(() => {
    const reference = comparison.value;
    if (!reference || reference.buckets.length === 0) return [];

    return METRIC_KEYS.filter((key) => metricDrawable.value[key]).flatMap((key) => {
        // Only what the reference could be drawn against at this granularity - a metric the halo is
        // not drawn for at all is not a coverage gap, and saying so would be reporting the absence of
        // something never claimed. See comparisonField.
        const field = comparisonField(key, granularity.value);
        if (field === null) return [];

        const days = reference.buckets.filter((bucket) => productivityPlottableValue(key, bucket[field]) !== null).map((bucket) => bucket.day_in_period);
        const metric = i18n.global.t(PRODUCTIVITY_METRICS[key].labelKey) as string;
        if (days.length === 0) return [i18n.global.t(`${REFERENCE_T}NoData`, { name: reference.name, metric }) as string];

        const firstDay = Math.min(...days);
        if (firstDay === 1) return [];
        return [i18n.global.t(`${REFERENCE_T}StartsLate`, { name: reference.name, metric, day: firstDay }) as string];
    });
});

// Both kinds of caveat in one strip, withheld readings first: one is a data-quality problem somebody
// may need to act on, the other a fact about the reference batch, and the chart reserves its height
// by counting the lines either way.
// Distinct from "no reading yet": a Section without an active weight Sensor will never populate
// the weight, Daily Gain or Growth Index bands, and one without a water Sensor never populates the
// independently-gated water band - so their toggles are disabled with a reason rather than left
// clickable for a band that can never draw, the same precedent SensorGatedKpiCell already sets for
// the row's KPI cells.
const hasWeightSensor = computed(() => data.value?.has_weight_sensor ?? false);

// The Section's cameras over the requested range, each with its own weight buckets. Empty when the
// Section has no weight Sensor; one entry when it has a single camera, which draws no camera curve at
// all - see drawsCameraSeries.
const cameras = computed(() => data.value?.cameras ?? []);
const hasWaterSensor = computed(() => data.value?.has_water_sensor ?? false);
const hasSensor = computed<Record<MetricSensor, boolean>>(() => ({ weight: hasWeightSensor.value, water: hasWaterSensor.value }));
const noSensorReason = computed(() => i18n.global.t('views.monitoring.activeGroups.texts.noSensor') as string);

// The only genuine empty state: the range admits no Production Group and carries no reading either. A
// Production Group with not one reading yet still has a span to draw, so it does not count as empty.
const isEmpty = computed(() => !isLoading.value && productionGroups.value.length === 0 && buckets.value.length === 0);

// Comparison: the single Production Group currently on the chart, or null when the range shows none
// or several - "this Section's current one" the dropdown's preceding-period candidate is relative
// to, and the timeline day-in-period buckets get re-projected onto. A range spanning several Study
// Periods is exactly the case the control goes unavailable for (see comparisonUnavailableReason
// below), since those periods are already on the axis in their own real dates.
const currentProductionGroup = computed(() => (productionGroups.value.length === 1 ? productionGroups.value[0] : null));
// The history read failing comes first: with no history there are no candidates either way, and
// "the range covers several groups" would be a reason the reader could act on for a control that
// would still be empty once they did.
const comparisonUnavailableReason = computed(() => {
    if (props.productionGroupHistoryUnavailable) {
        return i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareHistoryUnavailable') as string;
    }
    return productionGroups.value.length > 1
        ? (i18n.global.t('views.monitoring.activeGroups.modals.productivity.compareUnavailableMultiPeriod') as string)
        : null;
});
// Derived from productionGroupHistory, which the page above already loaded for the barn - no request
// of this component's own populates this list.
const comparisonCandidates = computed(() => resolveComparisonCandidates(props.productionGroupHistory, currentProductionGroup.value));
// The picked reference, resolved against the Section's whole history rather than against what the
// dropdown currently offers - so a range change that takes the control away does not silently
// abandon a pick that is still in the URL. The picker says it is being held; see its own template.
const selectedComparison = computed(() => resolveSelectedComparison(props.productionGroupHistory, selectedComparisonId.value));
// Which reference is actually fetched and drawn, as opposed to which is picked: nothing is drawn
// while the control is unavailable, since a range covering several Production Groups already has them on
// the axis in their own real dates and overlaying one of them again would draw it twice.
const activeComparisonId = computed(() => (comparisonUnavailableReason.value === null ? selectedComparison.value?.productionGroupId ?? null : null));

const { data: comparisonData } = useProductivityComparison(activeComparisonId, granularity);
// The reference the chart is drawing, as opposed to whatever that query last held. It keeps its
// previous result deliberately - switching reference should not blank the halo mid-flight - so a pick
// going away leaves the old result sitting in `data`, and every reader of it has to ask whether a
// reference is *picked* rather than whether data exists. Asked once, here, so the axis, the halo, the
// coverage caveats cannot answer it differently. Answered separately, stepping to the next Section
// in the barn leaves the previous one's halo, its projected stretch of axis and its coverage caveats
// on screen underneath a picker correctly reporting no reference at all.
const comparison = computed(() => (activeComparisonId.value === null ? null : comparisonData.value ?? null));
// The loaded reference as the axis sees it: which Production Group it is projected onto, and the
// instant its last re-projected day falls past. Its own length measured from the *displayed* Study
// Period's start, not from the compared Production Group's own calendar dates - which is what makes the
// projected stretch a continuation of the displayed period's own week and day numbering.
const comparisonProjection = computed<ComparisonProjection | null>(() => {
    if (!comparison.value || !currentProductionGroup.value || comparison.value.buckets.length === 0) return null;
    return {
        productionGroupId: currentProductionGroup.value.production_group_id,
        endMs: projectedComparisonEndMs(currentProductionGroup.value.start, comparison.value.length_days),
    };
});

// Which signals the reader keeps on, remembered between visits - in localStorage rather than in the
// URL, and per reader rather than per Section. Which metrics somebody reads is a habit ("I look at
// water first"), not the shared context a link carries: range, granularity and the comparison
// reference are that, and they travel as query parameters (see useProductivityViewState). Six
// booleans in the URL would also crowd out the three that mean something.
//
// Stored sparsely and merged over the registry's defaults on read, so a metric added to the registry
// later arrives switched to whatever that registry says rather than to `undefined` - and a reader who
// has never touched a chip still opens on the chart the product intends.
//
// The stored value is deliberately left untouched when a granularity takes a metric away - switching
// back restores what the reader had chosen rather than silently leaving it off, and the chip renders
// unchecked meanwhile so the control still tells the truth about what is on screen.
const storedMetrics = useLocalStorage<Partial<Record<MetricKey, boolean>>>('productivity:metrics', {});
const metricModels = Object.fromEntries(
    METRIC_KEYS.map((key) => [
        key,
        computed({
            get: () => storedMetrics.value[key] ?? PRODUCTIVITY_METRICS[key].defaultOn,
            // Written whole rather than mutated in place: useLocalStorage tracks the ref, not the
            // object behind it, so assigning a property persists nothing.
            set: (on: boolean) => {
                storedMetrics.value = { ...storedMetrics.value, [key]: on };
            },
        }),
    ])
) as Record<MetricKey, WritableComputedRef<boolean>>;

// Which metrics the current granularity can express at all, as opposed to which the user has asked
// for - see the registry's availableAtGranularity for the rules and why they live in one place
// rather than being re-tested at each of the sites that care (toggle, axis, series, readout, band
// layout, comparison halo).
const metricAvailableAtGranularity = computed(
    () => Object.fromEntries(METRIC_KEYS.map((key) => [key, PRODUCTIVITY_METRICS[key].availableAtGranularity(granularity.value)])) as MetricFlags
);

// The toggles as the rest of the component reads them: a metric counts as on only if it is both
// switched on and expressible at this granularity.
const effectiveToggles = computed(() => Object.fromEntries(METRIC_KEYS.map((key) => [key, metricModels[key].value && metricAvailableAtGranularity.value[key]])) as MetricFlags);

// Which metrics the chart is actually drawing: switched on, expressible at this granularity, and
// backed by a Sensor where the metric needs one. What the readout row's cells and the reference
// caveats below are both gated on.
//
// Deliberately not asked of the band layout, which answers the same question for the metrics that
// share a box: the caveats feed the chart's own height, the height feeds the band layout, and taking
// the layout's word for it closes that loop into a cycle. The two agree by construction - a band is
// visible exactly when one of its metrics is drawable.
const metricDrawable = computed(
    () =>
        Object.fromEntries(
            METRIC_KEYS.map((key) => {
                const metric = PRODUCTIVITY_METRICS[key];
                return [key, effectiveToggles.value[key] && (metric.sensor === null || hasSensor.value[metric.sensor])];
            })
        ) as MetricFlags
);

// Which bands are on screen: a band is drawn exactly when one of its own metrics is. Asked once, of
// the band rather than of any particular metric, so a metric added to a band is counted everywhere
// that matters without each of the three questions below having to be found and revisited.
const drawnBands = computed(() => PRODUCTIVITY_BAND_IDS.filter((band) => metricKeysInBand(band).some((key) => metricDrawable.value[key])));

// Nothing at all can be drawn: every metric is switched off, unexpressible at this granularity, or
// unbacked by a Sensor. Reached whenever those three between them account for all six - the reader
// clearing every toggle is the surest route, and a Section backed by neither a camera nor a water
// Sensor starts most of the way there. With
// zero bands the chart lays out several hundred pixels of blank white, the axis, the navigator and
// the Production Group band drawn around an empty middle; and no plot means no band to carry the
// per-band "no data" note, so the panel's one explanation goes with it. Hence a state of its own.
const nothingDrawable = computed(() => drawnBands.value.length === 0);

// Why, in the same words the disabled chips use. Only for the metrics the reader has actually asked
// for: a reason given for a metric they switched off themselves is noise, and when they have switched
// every one off there is nothing to explain - the list is empty and the state says just that.
const nothingDrawableReasons = computed(() => [
    ...new Set(
        METRIC_KEYS.filter((key) => metricModels[key].value)
            .map((key) => toggleDisabledReason(key))
            .filter((reason): reason is string => reason !== undefined)
    ),
]);

// The Alarms the chart draws, as opposed to the ones the Section has. A water Alarm is a mark on
// the water band's own axis: with that band off - switched off, unexpressible at this granularity,
// or unbacked by a Sensor - a red triangle would hang over a chart that shows nothing it could
// refer to. Turning Vand back on brings them back; nothing is filtered out of the payload, and the
// Section's Alarm history is unaffected by what this chart chooses to draw.
const drawnAlarms = computed(() => (drawnBands.value.includes('water') ? alarms.value : []));

// Which metric's explanation is showing, at most one at a time. Held here rather than left to the
// tooltip's own state because Radix opens on hover and on focus and deliberately not on tap: a finger
// has no hover, so on the tablets this panel is read on in a barn the six explanations would not
// exist. Driving `open` ourselves lets the circle's own click open it, while hover and focus keep
// working through the same binding.
const openExplanation = ref<MetricKey | null>(null);
function setExplanationOpen(key: MetricKey, open: boolean): void {
    if (open) openExplanation.value = key;
    // Only ever closes the one that is open: Radix emits a close for a tooltip whose pointer has left
    // just as another's click is opening it, and taking that at face value shuts the new one again.
    else if (openExplanation.value === key) openExplanation.value = null;
}

// No Sensor first: it is the more fundamental of the two reasons, and a Section without one would
// otherwise be told the metric is a granularity problem it could fix.
function toggleDisabledReason(key: MetricKey): string | undefined {
    const metric = PRODUCTIVITY_METRICS[key];
    if (metric.sensor !== null && !hasSensor.value[metric.sensor]) return noSensorReason.value;
    if (metricAvailableAtGranularity.value[key] || metric.unavailableAtGranularityKey === null) return undefined;
    return i18n.global.t(metric.unavailableAtGranularityKey) as string;
}

// The six signal toggles, declared rather than repeated six times in the template: each is the same
// control differing only in its label, its gating and the sentence explaining what it plots.
// `checked` is the effective state, not the stored one - a metric its granularity cannot express
// renders unchecked and disabled while the ref behind it keeps the user's own choice.
const metricToggles = computed(() =>
    METRIC_KEYS.map((key) => {
        const metric = PRODUCTIVITY_METRICS[key];
        return {
            key,
            group: metric.group,
            model: metricModels[key],
            label: i18n.global.t(metric.labelKey),
            disabled: (metric.sensor !== null && !hasSensor.value[metric.sensor]) || !metricAvailableAtGranularity.value[key],
            disabledReason: toggleDisabledReason(key),
            explanation: i18n.global.t(metric.explanationKey),
            checked: effectiveToggles.value[key],
        };
    })
);

// A Sensor the Section has, that reported nothing across the selected range: the band still draws
// (at a reduced height, carrying its own note) rather than vanishing, since "no reading" and
// "reading is flat" are different answers and only the band can tell them apart.
function bandIsEmpty(fields: ProductivityBucketField[]): boolean {
    return !buckets.value.some((bucket) => fields.some((field) => bucket[field] !== null));
}

const emptyBands = computed(() => {
    const fields = bandEmptinessFields(granularity.value);
    return Object.fromEntries(PRODUCTIVITY_BAND_IDS.map((band) => [band, bandIsEmpty(fields[band])])) as Record<MetricBandId, boolean>;
});

// Which empty bands state their own emptiness. While any drawn band still holds readings, each empty
// one says so - that is the case the note exists for, since a band drawing nothing and a band drawing
// a flat line are different answers and only the note tells them apart. Once every drawn band is
// empty the note has stopped being about a band and become a fact about the chart, so one band says
// it and the rest stay quiet: four boxes repeating the same sentence over an empty plot reads as four
// separate faults.
const noteBands = computed(() => {
    const empty = drawnBands.value.filter((band) => emptyBands.value[band]);
    return empty.length === drawnBands.value.length ? empty.slice(0, 1) : empty;
});

// Water at Day granularity has exactly one form - litres per pig - and that division needs a pig
// count, which a Section with no registered pigs in the range does not have. The band then draws
// nothing, which on its own reads as "this Section has no water" when the meter may be running
// perfectly: the litres are there, they are only expressible at Time. So the chart states the
// requirement rather than leaving an empty band to be read as an answer.
//
// A null dead count is the tell: it and the per-pig divisor come from the same pig tally, so no
// dead count for any day in the range means no tally to divide by.
const waterNeedsPigTallyCaveat = computed(() => {
    if (granularity.value !== 'day' || !hasWaterSensor.value || !emptyBands.value.water) return [];
    if (!buckets.value.every((bucket) => bucket.dead_count === null)) return [];
    return [i18n.global.t('views.monitoring.activeGroups.modals.productivity.waterNeedsPigTally') as string];
});

// Declared here rather than beside the two caveat sources above so that everything it reads is
// already in scope by the time it is: the water caveat needs the band emptiness computed just above.
const chartCaveats = computed(() => [
    ...withheldPointCaveats.value.map((text) => ({ text, tone: 'withheld' as const })),
    ...referenceCoverageCaveats.value.map((text) => ({ text, tone: 'coverage' as const })),
    ...waterNeedsPigTallyCaveat.value.map((text) => ({ text, tone: 'coverage' as const })),
]);

const { height: windowHeight } = useWindowSize();
const chartHeight = computed(() => productivityChartHeight(windowHeight.value, chartCaveats.value.length));
const plotHeightPx = computed(() => productivityPlotHeight(chartHeight.value));

const bandLayout = computed(() =>
    computeBandLayout(effectiveToggles.value, productionGroups.value.length > 0, hasWeightSensor.value, hasWaterSensor.value, feedMixes.value.length > 0, emptyBands.value, plotHeightPx.value)
);

// Every halo series for the currently loaded reference, one per metric, redrawn together whenever
// the pick or any of the visibility toggles change - "picking one redraws the reference for every
// visible metric at once". Empty array (nothing added to the chart's `series`) until a reference is
// picked and its data has loaded.
const haloSeries = computed(() => {
    if (!comparison.value || !currentProductionGroup.value || comparison.value.buckets.length === 0) return [];
    // The halo follows the primary chart's own visibility - a halo for a metric the chart is not
    // currently drawing has nothing to be read against - and the granularity decides which metrics
    // the reference can be drawn against at all (see comparisonField).
    // Section against Section, and nothing per camera. The reference's own camera series arrive in
    // the payload and are deliberately not drawn - see the `cameras` field on ProductivityComparison
    // for why an ordinal cannot carry a camera-to-camera comparison across two Production Groups.
    return buildComparisonHaloSeries(comparison.value.name, comparison.value.buckets, currentProductionGroup.value.start, effectiveToggles.value, granularity.value);
});

const eventTooltip = createProductivityEventTooltip(() => bandLayout.value);
const { readout, referenceReadout, isPinned, isPointing, releasePin, dropPin, onChartReady } = useProductivityCursor({
    buckets,
    granularity,
    toggles: metricDrawable,
    // Only while the reference is actually drawn: a figure quoted against a halo nobody can see is a
    // comparison the reader has no way to check.
    comparison,
    comparisonProductionGroupStart: computed(() => currentProductionGroup.value?.start ?? null),
    eventTooltip,
});

// The cameras the chart is actually leaving off the plot, as opposed to the ones the URL asked it to:
// a parameter survives a step to a Section with fewer cameras, and resolving it here means the row's
// dimmed lines and the curves on the plot can never disagree about which cameras are off.
const hiddenCameras = computed(() => resolveHiddenCameras(hiddenCameraSensorIds.value, cameras.value));

// Flipped against the resolved set, not the raw parameter - see toggleHiddenCamera for why.
function toggleCamera(sensorId: number) {
    hiddenCameraSensorIds.value = toggleHiddenCamera(hiddenCameras.value, sensorId);
}

// Whether the readout row is showing its per-camera breakdown. Component state rather than a query
// parameter, unlike the hidden cameras beside it: "camera 2 is the one lagging, here is the rest
// without it" is a finding worth sending on, whereas having the breakdown open is how this reader
// happens to be reading the row - the same line the six metric toggles sit on the other side of.
const camerasExpanded = ref(false);

// What each camera stated at the instant the row is describing. Derived from the readout rather than
// resolved alongside it, so a camera figure can never come from a different bucket than the Section
// figure it is shown under.
//
// Nothing at all unless the chart is drawing camera curves: on a single-camera Section the Section figure
// IS that camera's, so the breakdown would restate it with a gap of zero and offer to switch off the only
// camera there is.
const cameraReadout = computed(() => (drawsCameraSeries(cameras.value) ? cameraReadoutAt(cameras.value, readout.value?.timestamp ?? null) : []));

// The readout row's position chip, resolved against the same Production Groups and reference projection
// the x axis is numbered from - so the chip and the tick beneath the cursor always count from the
// same start. `nowMs` is the axis's own farm-local present, and only bounds how far an open Production Group is
// labelled; a bucket's instant never reaches it.
const readoutPeriodPosition = computed(() =>
    readout.value === null ? null : periodPositionLabel(productionGroups.value, readout.value.timestamp, nowMs.value, granularity.value, comparisonProjection.value)
);

// A pin describes one bucket of one Section over one range. Change any of those and it is pointing
// at something the reader never chose, so it is dropped rather than silently re-aimed.
watch([sectionId, range, granularity], dropPin);

const stockDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const chartOptions = computed(() =>
    buildProductivityOptions({
        base: stockDefaults.value as unknown as Record<string, unknown>,
        chartHeight: chartHeight.value,
        granularity: granularity.value,
        range: range.value,
        buckets: buckets.value,
        productionGroups: productionGroups.value,
        feedMixes: feedMixes.value,
        notes: notes.value,
        alarms: drawnAlarms.value,
        bandLayout: bandLayout.value,
        toggles: effectiveToggles.value,
        emptyBands: emptyBands.value,
        noteBands: noteBands.value,
        nowMs: nowMs.value,
        comparisonProjection: comparisonProjection.value,
        cameras: cameras.value,
        hiddenCameraSensorIds: hiddenCameras.value,
        haloSeries: haloSeries.value,
        eventTooltip,
        onRender: (chart) => productivityOverlay(chart).render({ bands: bandLayout.value }),
    })
);

// The segmented-control look shared by the range and granularity groups: two groups that pick one
// option out of a small fixed set do the same job, so they are the same control, single-sourced
// here rather than repeated per group where the two could drift apart.
// The metric chips. Deliberately the same pill vocabulary as the range and granularity controls
// beside them - all three pick from a small fixed set - but multi-select rather than one-of, which
// the filled/outlined pair carries. Each chip is a label and nothing else - no colour swatch
// doubling as the legend the chart has never had: the readout row already names every visible metric
// in its own colour directly above the plot, and on a filled chip two of the six colours are
// unreadable against the fill.
// On the wrapper rather than on a button, so `disabled:` variants have no element to fire on - the
// dimming is bound in the template instead.

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h3", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.title')), 1),
      _createVNode(_unref(DropdownMenu), { modal: false }, {
        default: _withCtx(() => [
          _createVNode(_unref(DropdownMenuTrigger), { class: "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50" }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.rangeHeading')), 1),
              _createTextVNode(" " + _toDisplayString(_ctx.$t(RANGE_OPTION_I18N_KEYS[_unref(rangeOption)])) + " ", 1),
              _createVNode(_unref(ChevronDown), {
                class: "h-3.5 w-3.5 text-gray-400",
                "aria-hidden": "true"
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(DropdownMenuContent), {
            align: "end",
            class: "min-w-[10rem]"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(DropdownMenuRadioGroup), {
                "model-value": _unref(rangeOption),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (rangeOption.value = $event as ProductivityRangeOption))
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(RANGE_OPTIONS), (option) => {
                    return (_openBlock(), _createBlock(_unref(DropdownMenuRadioItem), {
                      key: option,
                      value: option,
                      class: "cursor-pointer text-xs"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t(RANGE_OPTION_I18N_KEYS[option])), 1)
                      ]),
                      _: 2
                    }, 1032, ["value"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["model-value"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    (_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(Spinner, { class: "h-8 w-8" })
        ]))
      : (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.loadFailed')), 1),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("button", {
                type: "button",
                class: _normalizeClass([TOGGLE_BUTTON_CLASS, 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50']),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(refetch)()))
              }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.loadFailedRetry')), 3),
              (_unref(granularity) !== 'day')
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: _normalizeClass([TOGGLE_BUTTON_CLASS, 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50']),
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (granularity.value = 'day'))
                  }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.loadFailedReset')), 3))
                : _createCommentVNode("", true)
            ])
          ]))
        : (isEmpty.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("p", _hoisted_10, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.empty')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(metricToggles.value, (toggle, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: toggle.key
                    }, [
                      (index > 0 && toggle.group !== metricToggles.value[index - 1].group)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_14))
                        : _createCommentVNode("", true),
                      _createElementVNode("span", {
                        class: _normalizeClass([METRIC_CHIP_CLASS, toggle.checked ? METRIC_CHIP_ON_CLASS : METRIC_CHIP_OFF_CLASS, toggle.disabled ? 'opacity-40' : ''])
                      }, [
                        _createElementVNode("button", {
                          type: "button",
                          disabled: toggle.disabled,
                          "aria-pressed": toggle.checked,
                          class: "rounded-l-full py-1 pl-2.5 pr-1 outline-none disabled:cursor-not-allowed",
                          onClick: ($event: any) => (toggle.model.value = !toggle.model.value)
                        }, _toDisplayString(toggle.label), 9, _hoisted_15),
                        _createVNode(_unref(TooltipProvider), null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Tooltip), {
                              "delay-duration": 100,
                              open: openExplanation.value === toggle.key,
                              "onUpdate:open": ($event: any) => (setExplanationOpen(toggle.key, $event))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("button", {
                                      type: "button",
                                      class: "shrink-0 cursor-help rounded-r-full py-1 pl-0.5 pr-2.5 opacity-50 outline-none transition-opacity hover:opacity-100",
                                      "aria-label": _ctx.$t('views.monitoring.activeGroups.modals.productivity.explainAria', { name: toggle.label }),
                                      onClick: ($event: any) => (setExplanationOpen(toggle.key, openExplanation.value !== toggle.key))
                                    }, [
                                      _createVNode(_unref(Info), {
                                        class: "h-3 w-3",
                                        "aria-hidden": "true"
                                      })
                                    ], 8, _hoisted_16)
                                  ]),
                                  _: 2
                                }, 1024),
                                _createVNode(_unref(TooltipContent), { class: "max-w-[18rem] text-xs font-normal leading-snug text-gray-700" }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(toggle.disabled ? toggle.disabledReason : toggle.explanation), 1)
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["open", "onUpdate:open"])
                          ]),
                          _: 2
                        }, 1024)
                      ], 2)
                    ], 64))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createVNode(ProductivityComparisonPicker, {
                    selected: selectedComparison.value,
                    candidates: comparisonCandidates.value,
                    "unavailable-reason": comparisonUnavailableReason.value,
                    onReferencePicked: _cache[3] || (_cache[3] = ($event: any) => (selectedComparisonId.value = $event?.productionGroupId ?? null))
                  }, null, 8, ["selected", "candidates", "unavailable-reason"]),
                  _createElementVNode("div", {
                    class: _normalizeClass(TOGGLE_GROUP_CLASS)
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(granularityOptions), (option) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: option,
                        type: "button",
                        class: _normalizeClass([TOGGLE_BUTTON_CLASS, _unref(granularity) === option ? TOGGLE_BUTTON_ACTIVE_CLASS : TOGGLE_BUTTON_IDLE_CLASS]),
                        onClick: ($event: any) => (granularity.value = option)
                      }, _toDisplayString(_ctx.$t(`views.monitoring.activeGroups.texts.${option}`)), 11, _hoisted_18))
                    }), 128))
                  ])
                ])
              ]),
              (!nothingDrawable.value)
                ? (_openBlock(), _createBlock(ProductivityReadoutRow, {
                    key: 0,
                    readout: _unref(readout),
                    granularity: _unref(granularity),
                    visible: metricDrawable.value,
                    reference: _unref(referenceReadout),
                    "period-position": readoutPeriodPosition.value,
                    pointing: _unref(isPointing),
                    pinned: _unref(isPinned),
                    cameras: cameraReadout.value,
                    "cameras-expanded": camerasExpanded.value,
                    "hidden-sensor-ids": hiddenCameras.value,
                    onRelease: _unref(releasePin),
                    onToggleCameras: _cache[4] || (_cache[4] = ($event: any) => (camerasExpanded.value = !camerasExpanded.value)),
                    onToggleCamera: toggleCamera
                  }, null, 8, ["readout", "granularity", "visible", "reference", "period-position", "pointing", "pinned", "cameras", "cameras-expanded", "hidden-sensor-ids", "onRelease"]))
                : _createCommentVNode("", true),
              (nothingDrawable.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createElementVNode("p", _hoisted_20, _toDisplayString(nothingDrawableReasons.value.length
                            ? _ctx.$t('views.monitoring.activeGroups.modals.productivity.nothingDrawable')
                            : _ctx.$t('views.monitoring.activeGroups.modals.productivity.noMetricsOn')), 1),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nothingDrawableReasons.value, (reason) => {
                      return (_openBlock(), _createElementBlock("p", {
                        key: reason,
                        class: "text-xs text-gray-400"
                      }, _toDisplayString(reason), 1))
                    }), 128)),
                    (_unref(granularity) !== 'day')
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: _normalizeClass([TOGGLE_BUTTON_CLASS, 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50']),
                          onClick: _cache[5] || (_cache[5] = ($event: any) => (granularity.value = 'day'))
                        }, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.productivity.loadFailedReset')), 3))
                      : _createCommentVNode("", true)
                  ]))
                : (_openBlock(), _createBlock(GenericChart, {
                    key: 2,
                    options: (chartOptions.value as any),
                    onReady: _unref(onChartReady),
                    onUpdate: _unref(onChartReady)
                  }, null, 8, ["options", "onReady", "onUpdate"])),
              (chartCaveats.value.length)
                ? (_openBlock(), _createElementBlock("p", _hoisted_21, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chartCaveats.value, (caveat) => {
                      return (_openBlock(), _createElementBlock("span", {
                        key: caveat.text,
                        class: _normalizeClass(["block", caveat.tone === 'withheld' ? 'text-amber-700' : 'text-gray-500'])
                      }, _toDisplayString(caveat.text), 3))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]))
  ]))
}
}

})